import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { wishlistService } from "@/services/wishlist.service";

/**
 * Hook de favoritos compartilhado via React Query — todos os componentes
 * que chamam `useWishlist()` reaproveitam o mesmo cache (uma única
 * requisição para a lista de IDs, mesmo que existam N ProductCards na tela).
 *
 * Estados:
 * - `requiresLogin`: usuário precisa logar antes de favoritar.
 * - `ids`: Set de product_ids favoritados.
 */
const EMPTY_SET: Set<string> = new Set();

export const useWishlist = () => {
  const { user, authReady } = useAuth();
  const qc = useQueryClient();

  const { data: ids = EMPTY_SET, isLoading: loading } = useQuery<Set<string>>({
    queryKey: ["wishlist-ids", user?.id ?? null],
    queryFn: async () => {
      if (!user) return new Set<string>();
      const list = await wishlistService.listIds(user.id);
      return new Set(list);
    },
    enabled: authReady,
    staleTime: 60_000,
  });

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["wishlist-ids", user?.id ?? null] });
  }, [qc, user?.id]);

  const toggle = useCallback(
    async (productId: string) => {
      if (!user) return { ok: false as const, requiresLogin: true as const };
      const isFav = ids.has(productId);
      const key = ["wishlist-ids", user.id] as const;
      // Optimistic update
      qc.setQueryData<Set<string>>(key, (prev) => {
        const next = new Set(prev ?? []);
        if (isFav) next.delete(productId);
        else next.add(productId);
        return next;
      });
      try {
        if (isFav) await wishlistService.remove(user.id, productId);
        else await wishlistService.add(user.id, productId);
        // Invalida a lista "rica" (com produtos) usada na página /favoritos
        qc.invalidateQueries({ queryKey: ["wishlist", user.id] });
        return { ok: true as const, isFav: !isFav };
      } catch (e) {
        // rollback
        qc.setQueryData<Set<string>>(key, (prev) => {
          const next = new Set(prev ?? []);
          if (isFav) next.add(productId);
          else next.delete(productId);
          return next;
        });
        return { ok: false as const, error: e as Error };
      }
    },
    [ids, user, qc]
  );

  const requiresLogin = !user;
  return useMemo(
    () => ({ ids, loading, toggle, refresh, requiresLogin }),
    [ids, loading, toggle, refresh, requiresLogin]
  );
};